import { usePpraSurveyControllerCreateLead } from '@/api/generated/ppra/ppra';
import { useSessionControllerGetOrCreateSession } from '@/api/generated/session/session';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import authService from '@/services/auth.service';
import { useToast } from '@cmpsr/components';
import { useCallback, useRef, useState } from 'react';
import useAnalyticsCalls from './use-analytics';

export type Session = {
  token: string;
  email?: string;
};

export type LeadAndName = {
  email: string;
  inviteCode?: string;
  firstName?: string;
};

export default function useVisitorSession() {
  const toast = useToast();
  const [sessionToken, setSessionToken] = useState<string | null>(authService.getVisitorToken());
  const [email, setEmail] = useState<string | null>(authService.getVisitorEmail());
  const isInitializingRef = useRef(false);
  const { mutateAsync: getOrCreateSession } = useSessionControllerGetOrCreateSession();
  const createLeadMutation = usePpraSurveyControllerCreateLead();
  const { callIdentifySignUp } = useAnalyticsCalls();

  const getSession = useCallback(async (): Promise<Session | null> => {
    // If we have a session token, email, and session id, return the session
    if (sessionToken && email) {
      const response: Session = {
        token: sessionToken,
        email: email,
      };
      return response;
    }

    const response = await getOrCreateSession();
    authService.loginVisitor({ token: response.token });
    if (response.email) {
      authService.setVisitorEmail(response.email);
      setEmail(response.email);
    }
    setSessionToken(response.token);

    return {
      token: response.token,
      email: response.email,
    };
  }, [getOrCreateSession, sessionToken, email]);

  // Initialize or retrieve a visitor session
  const initializeSession = useCallback<() => Promise<Session | null>>(async () => {
    // If already initializing don't initialize again
    if (isInitializingRef.current) {
      return null;
    }

    isInitializingRef.current = true;

    try {
      return await getSession();
    } catch (error) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Session Error',
        description: 'Failed to initialize visitor session',
        status: 'error',
      });
    } finally {
      isInitializingRef.current = false;
    }
    return null;
  }, [getSession, toast]);

  const captureLead = useCallback(
    async (email: string, inviteCode: string | undefined) => {
      const response = await createLeadMutation.mutateAsync({
        data: {
          email,
          inviteCode,
        },
      });

      authService.setVisitorEmail(email);
      if (response.inviteCode) {
        authService.setInviteCode(response.inviteCode);
        callIdentifySignUp(response.userId, email, 'waitlist');
      } else {
        // If the invite code is not found, clear the invite code
        authService.clearInviteCode();
        callIdentifySignUp(response.userId, email, 'lead');
      }
      return response;
    },
    [createLeadMutation, callIdentifySignUp]
  );

  const captureLeadAndName = useCallback(
    async (leadAndName: LeadAndName) => {
      if (leadAndName.firstName) {
        authService.setVisitorFirstName(leadAndName.firstName);
      }
      await captureLead(leadAndName.email, leadAndName.inviteCode);
    },
    [captureLead]
  );

  // Clear the visitor session
  const logoutSession = () => {
    authService.logoutVisitor();
    setSessionToken(null);
  };

  // Clear the visitor session completely including email
  const clearSession = () => {
    authService.clearVisitor();
    setSessionToken(null);
    setEmail(null);
  };

  return {
    sessionToken,
    email,
    initializeSession,
    logoutSession,
    clearSession,
    isInitializingSession: isInitializingRef.current,
    captureLead,
    captureLeadAndName,
    getSession,
  };
}
